import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Bike, Store, Banknote, Landmark, User, MapPin, Clock, Wallet, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useMenu } from "@/hooks/useMenu";
import { useCart } from "@/context/CartContext";
import { http, errMsg, formatARS } from "@/lib/api";
import { buildComanda, waLink } from "@/lib/whatsapp";
import { Field, Card, Toggle2 } from "@/components/checkout/Field";
import { TransferInfo } from "@/components/checkout/TransferInfo";

const EMPTY = { name: "", phone: "", address: "", number: "", between: "", reference: "", notes: "", cash: "" };

export default function CheckoutPage() {
  const { data } = useMenu();
  const { items, total: subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [type, setType] = useState("retiro");
  const [pay, setPay] = useState("efectivo");
  const [receipt, setReceipt] = useState("");
  const [sending, setSending] = useState(false);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  const s = data.settings;
  const fee = type === "delivery" ? Number(s.delivery_fee) || 0 : 0;
  const total = subtotal + fee;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!items.length) return toast.error("Tu carrito está vacío");
    if (!f.name.trim() || !f.phone.trim()) return toast.error("Completá tu nombre y teléfono");
    if (type === "delivery" && ["address", "number", "between", "reference"].some((k) => !f[k].trim())) return toast.error("Completá todos los datos de envío");
    if (pay === "efectivo" && f.cash && Number(f.cash) < total) return toast.error("El monto en efectivo es menor al total");
    if (pay === "transferencia" && !receipt) return toast.error("Adjuntá la foto del comprobante");
    if (String(s.whatsapp || "").replace(/\D/g, "").length < 8) return toast.error("El local aún no configuró su WhatsApp");
    const order = {
      items: items.map(({ name, details, unit_price, qty }) => ({ name, details, unit_price, qty })),
      total, customer_name: f.name.trim(), customer_phone: f.phone.trim(), delivery_type: type,
      delivery: type === "delivery" ? { address: f.address, number: f.number, between: f.between, reference: f.reference } : {},
      payment_method: pay, cash_amount: pay === "efectivo" && f.cash ? Number(f.cash) : null,
      receipt: pay === "transferencia" ? receipt : "", notes: f.notes.trim(),
    };
    setSending(true);
    const win = window.open("", "_blank");
    try {
      const { data: res } = await http.post("/orders", order);
      const url = waLink(s.whatsapp, buildComanda({ settings: s, order, number: res.number, subtotal, fee }));
      if (win) win.location.href = url;
      clear();
      navigate("/pedido-enviado", { replace: true, state: { url, number: res.number, transfer: pay === "transferencia", type, wait: s.wait_time, address: s.address } });
    } catch (e) {
      if (win) win.close();
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen pb-36" data-testid="checkout-page">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#120E0E]/85 border-b border-[#2E2323]">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
          <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-white/5" data-testid="checkout-back-btn"><ArrowLeft className="w-5 h-5" /></Link>
          <h1 className="text-xl font-extrabold">Finalizar pedido</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 mt-5 space-y-4">
        <Card title="Tus datos" icon={User}>
          <Field label="Nombre y apellido" required value={f.name} onChange={set("name")} testid="checkout-name-input" placeholder="Ej: Juan Pérez" />
          <Field label="Teléfono" required type="tel" value={f.phone} onChange={set("phone")} testid="checkout-phone-input" placeholder="Ej: 11 2345 6789" />
        </Card>

        <Card title="Entrega" icon={MapPin}>
          <Toggle2 prefix="delivery-type" value={type} onChange={setType} options={[{ value: "retiro", label: "Retiro en local", icon: Store }, { value: "delivery", label: "Delivery", icon: Bike }]} />
          {type === "retiro" ? (
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 space-y-2 text-sm" data-testid="pickup-info">
              <p className="flex gap-2"><MapPin className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /> <span>Retirá en: <b>{s.address}</b></span></p>
              <p className="flex gap-2"><Clock className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /> <span>Tiempo estimado de espera: <b>{s.wait_time}</b></span></p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="delivery-fields">
              <Field label="Dirección de envío" required value={f.address} onChange={set("address")} testid="delivery-address-input" placeholder="Calle" />
              <Field label="Número de casa / piso" required value={f.number} onChange={set("number")} testid="delivery-number-input" placeholder="Ej: 1234, 2° B" />
              <Field label="Entre calles" required value={f.between} onChange={set("between")} testid="delivery-between-input" placeholder="Ej: Mitre y Belgrano" />
              <Field label="Referencia" required value={f.reference} onChange={set("reference")} testid="delivery-reference-input" placeholder='Ej: "puerta verde, timbre B"' />
              <p className="text-xs text-[#A89F91] flex gap-2"><Clock className="w-4 h-4 text-amber-400" /> Tiempo estimado: {s.wait_time}</p>
            </div>
          )}
          <Field label="Notas del pedido (opcional)" textarea value={f.notes} onChange={set("notes")} testid="checkout-notes-input" placeholder="Ej: sin aceitunas, gustos de empanadas..." />
        </Card>

        <Card title="Pago" icon={Wallet}>
          <Toggle2 prefix="payment-method" value={pay} onChange={setPay} options={[{ value: "efectivo", label: "Efectivo", icon: Banknote }, { value: "transferencia", label: "Transferencia", icon: Landmark }]} />
          {pay === "efectivo" ? (
            <div data-testid="cash-info">
              <Field label="¿Con cuánto abonás? (para el cambio)" type="number" inputMode="numeric" value={f.cash} onChange={set("cash")} testid="cash-amount-input" placeholder={`Ej: ${Math.ceil(total / 1000) * 1000}`} />
              {Number(f.cash) >= total && <p className="text-xs text-amber-300 mt-2" data-testid="cash-change">Tu vuelto: {formatARS(Number(f.cash) - total)}</p>}
            </div>
          ) : (
            <TransferInfo settings={s} receipt={receipt} setReceipt={setReceipt} />
          )}
        </Card>

        <Card title="Resumen">
          {items.map((it) => (
            <div key={it.key} className="flex justify-between gap-3 text-sm">
              <span className="text-[#d9cfc2]">{it.qty}x {it.name}{it.details?.length ? <span className="block text-xs text-[#A89F91]">{it.details.join(" · ")}</span> : null}</span>
              <span className="shrink-0">{formatARS(it.unit_price * it.qty)}</span>
            </div>
          ))}
          {fee > 0 && <div className="flex justify-between text-sm text-[#A89F91]"><span>Envío</span><span>{formatARS(fee)}</span></div>}
          <div className="flex justify-between items-end pt-3 border-t border-[#2E2323]">
            <span className="font-semibold">Total</span>
            <span className="font-price text-3xl text-amber-400" data-testid="checkout-total">{formatARS(total)}</span>
          </div>
        </Card>
      </main>
      <div className="fixed bottom-0 inset-x-0 z-40 p-4 backdrop-blur-md bg-[#120E0E]/85 border-t border-[#2E2323]">
        <button onClick={submit} disabled={sending} data-testid="checkout-submit-btn"
          className="max-w-2xl mx-auto w-full py-4 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-[#0b2a17] font-extrabold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Enviar pedido por WhatsApp · {formatARS(total)}
        </button>
      </div>
    </div>
  );
}
